import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLang } from '../context/LanguageContext';
import { toast } from 'sonner';
import {
  Send, Sparkles, Mic, Wand2,
  Globe, RefreshCw, CheckCircle2, Lock, X,
  Music2, Zap, Clock, ChevronLeft, Plus, MessageSquare, Trash2, FolderOpen,
  ImageIcon, Loader2
} from 'lucide-react';
import ProducerDAWView from '../components/producer/ProducerDAWView';
import ProducerDJPanel from '../components/producer/ProducerDJPanel';
import SmartCreativePanel from '../components/producer/SmartCreativePanel';
import VocalRecorder from '../components/producer/VocalRecorder';
import ProducerEntryScreen from '../components/producer/ProducerEntryScreen';
import ProducerUploadFlow from '../components/producer/ProducerUploadFlow';
import CorcheaAnimation from '../components/producer/CorcheaAnimation';
import { generateDefaultSections, recalcSections } from '../components/producer/StructureTimeline';
import { VersionTimeline } from '../components/producer/VersionTimeline';
import ProducerSmartButtons from '../components/producer/ProducerSmartButtons';
import StructureQuickActions from '../components/producer/StructureQuickActions';
import IntentEngine from '../components/producer/IntentEngine';
import GenerationControls from '../components/producer/GenerationControls';
import SamplePicker from '../components/producer/SamplePicker';
import { useLegalGate } from '../components/LegalAcceptanceModal';
import StudioOnboarding from '../components/producer/StudioOnboarding';
import { StudioCompanionBubble } from '../components/producer/StudioCompanionBubble';
import PublishPanel from '../components/producer/PublishPanel';
import { ProducerComingSoonScreen, PRODUCER_UNLOCK_EMAIL } from '../components/ProducerComingSoon';
import { API_BASE as API } from '../lib/apiBase';
import '../styles/producer.css';

const CorcheaIcon = ({ size = 14, className = '' }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M12 3a1 1 0 011 1v10.27A4 4 0 1111 18.13V4a1 1 0 011-1zm0 17a2 2 0 100-4 2 2 0 000 4z" />
    <path d="M13 4h5v3h-5V4z" />
  </svg>
);

const STEPS = [
  { key: 'base',    name: 'Base',    Icon: Music2, doneKey: 'base_done', color: '#F5C842', cost: 5 },
  { key: 'voice',   name: 'Voz',     Icon: Mic,    doneKey: 'voice_done',color: '#00D4FF', cost: 4 },
  { key: 'improve', name: 'Mejoras', Icon: Wand2,  doneKey: 'improved',  color: '#7000FF', cost: 3 },
];

const QUICK_PROMPTS = [
  'Algo oscuro y melancólico',
  'Hit de verano con energía',
  'Trap introspectivo',
  'Electrónica épica',
  'Reggaetón romántico',
  'Sorpréndeme',
];

function renderMarkdown(text) {
  return text
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/_(.+?)_/g, '<em>$1</em>')
    .replace(/\n/g, '<br/>');
}

export default function ProducerPage() {
  const { token, user } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();

  const [studioTourDone, setStudioTourDone] = useState(() => {
    try {
      return typeof localStorage !== 'undefined' && localStorage.getItem('pulse_studio_onboarding_done') === '1';
    } catch {
      return false;
    }
  });
  // ── Phase: entry → upload | studio ──
  const [phase, setPhase] = useState('entry');
  const [studioMode, setStudioMode] = useState('create');
  /* studioMode: 'create' | 'improve' — used for future conditional UI */

  // ── Session & project state ──
  const [sessionId, setSessionId] = useState(null);
  const sessionIdRef = useRef(null);
  const [messages, setMessages] = useState([]);
  const [stages, setStages] = useState({ base_done: false, voice_done: false, improved: false, published: false });
  const [project, setProject] = useState({});
  const [version, setVersion] = useState(1);
  const [versions, setVersions] = useState([]);

  // ── Structure (single source of truth for song arrangement) ──
  const [sections, setSections] = useState([]);
  const [defaultSections, setDefaultSections] = useState([]);
  const [loopRegion, setLoopRegion] = useState(null);

  // ── Wallet ──
  const [wallet, setWallet] = useState({ balance: 0, daily_ads_claimed: 0, daily_ads_date: '', has_suno: false, has_fish_audio: false });
  const [packs, setPacks] = useState([]);

  // ── Chat ──
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);

  // ── Generation ──
  const [generating, setGenerating] = useState(null);
  const [taskId, setTaskId] = useState(null);
  const [taskPolling, setTaskPolling] = useState(false);
  const [musicProvider, setMusicProvider] = useState('suno');
  const taskPollRef = useRef(null);

  // ── Audio URL (WaveSurfer handles playback inside DAW) ──
  const [currentAudio, setCurrentAudio] = useState(null);
  const playerRef = useRef(null);

  // ── DJ / musical params ──
  const [bpm, setBpm] = useState(120);
  const [musicalKey, setMusicalKey] = useState('Am');
  const [dawContext, setDawContext] = useState({});
  
  // ── EQ/FX state (synced with DJ Panel) ──
  const [eqValues, setEqValues] = useState({
    bass: 5, mid: 5, treble: 5,
    reverb: 0, delay: 0, filter: 20,
  });
  
  // Forward EQ/FX changes from DJ Panel → WaveSurfer player
  const handleEQChange = useCallback((param, value) => {
    setEqValues(prev => ({ ...prev, [param]: value }));
    const p = playerRef.current;
    if (!p) return;
    if (param === 'bass' || param === 'mid' || param === 'treble') {
      p.setEQ?.(param, value);
    }
    if (param === 'reverb') p.setReverb?.(value);
    if (param === 'delay') p.setDelay?.(value);
    if (param === 'filter') p.setFilter?.(value);
  }, []);

  // ── UI ──
  const [mobileTab, setMobileTab] = useState('chat');
  const [showPackModal, setShowPackModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [publishTitle, setPublishTitle] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [claimingAd, setClaimingAd] = useState(false);
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [publishedSongId, setPublishedSongId] = useState(null);
  const [projectList, setProjectList] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sampleGroup, setSampleGroup] = useState(null);
  const [previewState, setPreviewState] = useState({ enabled: false, mainAudio: null });
  const [playbackTime, setPlaybackTime] = useState(0);
  const [seekRequest, setSeekRequest] = useState(null);
  /** Stem locks + vocal protect — sent to API so prompts respect user intent (additive UX). */
  const [lockedStems, setLockedStems] = useState({ drums: false, bass: false, vocals: false, other: false });
  const [vocalLocked, setVocalLocked] = useState(true);
  const mobileDawHasPrimaryContent = !!currentAudio || !!taskPolling || !!generating || !!stages.base_done || !!stages.voice_done || !!stages.improved;
  const [backendReachable, setBackendReachable] = useState(null);
  const [providerStatus, setProviderStatus] = useState({ hasSuno: true, hasFishAudio: true, loaded: false });

  // ── Corchea animations ──
  const [corcheaAnim, setCorcheaAnim] = useState(null);
  const prevBalance = useRef(wallet.balance);
  const pendingMsgRef = useRef(null);

  const messagesEndRef = useRef(null);

  // ── Auth redirect ──
  useEffect(() => { if (!token) navigate('/login'); }, [token, navigate]);

  // ── Backend health probe (prevents silent "chat-only" degraded mode in QA/prod) ──
  useEffect(() => {
    let mounted = true;
    const probe = async () => {
      try {
        const r = await fetch(`${API}/api/health`, { method: 'GET' });
        if (mounted) setBackendReachable(r.ok);
      } catch {
        if (mounted) setBackendReachable(false);
      }
    };
    probe();
    const timer = setInterval(probe, 15000);
    return () => { mounted = false; clearInterval(timer); };
  }, []);

  // ── Legal gate for AI generation ──
  const legalAI = useLegalGate('generate_ai');

  // ── Init on mount ──
  useEffect(() => {
    if (!token) return;
    fetchWallet();
    fetchPacks();
    fetchProjects();
  }, [token]);

  const fetchProjects = async () => {
    try {
      const r = await fetch(`${API}/api/producer/sessions`, { headers });
      if (r.ok) {
        const data = await r.json();
        setProjectList(data.sessions || []);
      }
    } catch {}
  };

  // ── Auto-scroll messages ──
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  // ── Send pending message after session init ──
  useEffect(() => {
    if (phase === 'studio' && pendingMsgRef.current && sessionIdRef.current && token) {
      const msg = pendingMsgRef.current;
      pendingMsgRef.current = null;
      const timer = setTimeout(() => sendMessage(msg), 300);
      return () => clearTimeout(timer);
    }
  }, [phase, sessionId, token]);

  // ── Corchea animation on balance increase ──
  useEffect(() => {
    if (wallet.balance > prevBalance.current && prevBalance.current > 0) {
      const diff = wallet.balance - prevBalance.current;
      setCorcheaAnim({ amount: diff, key: `c-${Date.now()}` });
    }
    prevBalance.current = wallet.balance;
  }, [wallet.balance]);

  // ── Poll music generation task ──
  useEffect(() => {
    if (!taskId) return;
    setTaskPolling(true);
    taskPollRef.current = setInterval(async () => {
      try {
        const r = await fetch(`${API}/api/producer/task/${taskId}?provider=${musicProvider}`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await r.json();
        if (data.status === 'done') {
          clearInterval(taskPollRef.current);
          setTaskPolling(false);
          setTaskId(null);
          setGenerating(null);
          if (data.audio_url) {
            setCurrentAudio(data.audio_url);
            setProject(prev => {
              const step = generating || 'base';
              return { ...prev, [step]: { ...prev[step], audio_url: data.audio_url, audio_status: 'done', image_url: data.image_url, title: data.title || prev.title } };
            });
            toast.success('Tu música está lista.', { duration: 5000 });
            stopGenTimer();
            // Update stages to reflect base_done
            setStages(prev => ({ ...prev, base_done: true }));
            // Initialize structure sections for the generated track
            const trackDuration = data.duration || 180;
            const genre = project?.style?.toLowerCase() || 'default';
            initSections(genre, trackDuration);
            setMessages(prev => [...prev, {
              role: 'assistant',
              content: `Tu **${data.title || 'canción'}** está lista. ${data.duration ? `Duración: ${Math.round(data.duration)}s.` : ''}\n\nPulsa play para escucharla.`,
              timestamp: new Date().toISOString(),
              isGeneration: true,
            }]);
          }
        } else if (data.status === 'error' || data.status === 'no_provider_configured') {
          clearInterval(taskPollRef.current);
          setTaskPolling(false);
          setTaskId(null);
          setGenerating(null);
          if (data.status === 'no_provider_configured') {
            toast.error(lang === 'es'
              ? 'El generador de música no está disponible ahora. El equipo lo está preparando.'
              : 'The music generator isn’t available right now. We’re getting it ready.');
          } else {
            toast.error('Error generando. Reintenta.');
          }
          stopGenTimer();
        }
      } catch {}
    }, 8000);
    return () => clearInterval(taskPollRef.current);
  }, [taskId, token]);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  // ── API Functions ──
  const fetchWallet = async () => {
    try {
      const r = await fetch(`${API}/api/producer/wallet`, { headers });
      if (r.ok) {
        const d = await r.json();
        setWallet(d);
        setProviderStatus({
          hasSuno: !!d.has_suno,
          hasFishAudio: !!d.has_fish_audio,
          loaded: true,
        });
        if (d.welcome_bonus > 0) {
          setCorcheaAnim({ amount: d.welcome_bonus, key: `wb-${Date.now()}` });
          toast.success(`+${d.welcome_bonus} corcheas de bienvenida`, { duration: 5000 });
        }
      }
    } catch {}
  };

  const fetchPacks = async () => {
    try {
      const r = await fetch(`${API}/api/producer/packs`);
      if (r.ok) { const d = await r.json(); setPacks(d.packs || []); }
    } catch {}
  };

  const fetchVersions = async (sid) => {
    const sessId = sid || sessionIdRef.current || sessionId;
    if (!sessId || !token) return;
    try {
      const r = await fetch(`${API}/api/producer/sessions/${sessId}/versions`, { headers });
      if (r.ok) {
        const d = await r.json();
        setVersions(d.versions || []);
      }
    } catch {}
  };

  const restoreVersion = async (versionIdx) => {
    const sid = sessionIdRef.current || sessionId;
    if (!sid || !token) return;
    try {
      const r = await fetch(`${API}/api/producer/sessions/${sid}/restore/${versionIdx}`, {
        method: 'POST', headers,
      });
      const data = await r.json();
      if (r.ok) {
        setVersion(data.current_version || data.restored_version || version);
        if (data.project) {
          setProject(prev => ({ ...prev, ...data.project }));
          if (data.project.bpm) setBpm(data.project.bpm);
          if (data.project.key) setMusicalKey(data.project.key.replace(' major', '').replace(' minor', 'm'));
        }
        if (data.audio_url) setCurrentAudio(data.audio_url);
        toast.success(`Restaurada v${data.restored_version}`);
        fetchVersions(sid);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `Restaurada **v${data.restored_version}**. Escucha el resultado.`,
          timestamp: new Date().toISOString(),
        }]);
      } else {
        toast.error(data.detail || 'Error restaurando versión');
      }
    } catch { toast.error('Error de conexión'); }
  };

  // ── Structure management ──
  const initSections = useCallback((genre, dur = 180) => {
    const generated = generateDefaultSections(genre || 'default', dur, bpm);
    setSections(generated);
    setDefaultSections(generated);
  }, [bpm]);

  const handleSectionsChange = useCallback((newSections) => {
    setSections(newSections);
  }, []);

  const initSession = useCallback(async (selectedStyle = '') => {
    try {
      const r = await fetch(`${API}/api/producer/sessions/new`, {
        method: 'POST', headers,
        body: JSON.stringify({ lang, title: '' }),
      });
      if (r.ok) {
        const session = await r.json();
        const sid = session.session_id;
        setSessionId(sid);
        sessionIdRef.current = sid;
        setMessages(session.messages || []);
        setStages(session.stages || { base_done: false, voice_done: false, improved: false, published: false });
        setProject(session.project || { style: selectedStyle });
        setCurrentAudio(null);
        setPublishTitle('');
        fetchProjects();
        return sid;
      }
      const data = await r.json().catch(() => ({}));
      toast.error(data.detail || (lang === 'es' ? 'Backend de Producer no disponible.' : 'Producer backend unavailable.'));
      return null;
    } catch {
      toast.error(lang === 'es' ? 'No se pudo iniciar sesión de Producer (backend caído).' : 'Could not start Producer session (backend down).');
      return null;
    }
  }, [lang, headers]);

  // ── Chips state for contextual suggestions ──
  const [chips, setChips] = useState([]);
  const [genTimer, setGenTimer] = useState(0);
  const genTimerRef = useRef(null);
  const [genMessage, setGenMessage] = useState('');

  const GEN_MESSAGES_ES = [
    'Componiendo la base...', 'Ajustando el groove...', 'Mezclando frecuencias...',
    'Creando la melodia...', 'Produciendo el beat...', 'Afinando el sonido...',
    'Masterizando...', 'Dando forma al track...',
  ];
  const GEN_MESSAGES_EN = [
    'Composing the base...', 'Adjusting the groove...', 'Mixing frequencies...',
    'Creating the melody...', 'Producing the beat...', 'Tuning the sound...',
    'Mastering...', 'Shaping the track...',
  ];

  // Start/stop gen timer
  const startGenTimer = useCallback(() => {
    setGenTimer(0);
    const msgs = lang === 'es' ? GEN_MESSAGES_ES : GEN_MESSAGES_EN;
    setGenMessage(msgs[0]);
    genTimerRef.current = setInterval(() => {
      setGenTimer(prev => {
        const next = prev + 1;
        const msgIdx = Math.floor(next / 5) % msgs.length;
        setGenMessage(msgs[msgIdx]);
        return next;
      });
    }, 1000);
  }, [lang]);

  const stopGenTimer = useCallback(() => {
    if (genTimerRef.current) {
      clearInterval(genTimerRef.current);
      genTimerRef.current = null;
    }
    setGenTimer(0);
    setGenMessage('');
  }, []);

  // ── Send message — PULSE brain decides action ──
  const sendMessage = useCallback(async (text) => {
    const msg = (text || input).trim();
    if (!msg || sending) return;
    const sid = sessionIdRef.current || sessionId;
    if (!sid) {
      toast.error(lang === 'es' ? 'Inicia un proyecto primero.' : 'Start a project first.');
      return;
    }
    setInput('');
    setChips([]);
    setSending(true);
    if (!msg.startsWith('[estructura]')) {
      setMessages(prev => [...prev, { role: 'user', content: msg, timestamp: new Date().toISOString() }]);
    }
    try {
      const r = await fetch(`${API}/api/producer/chat`, {
        method: 'POST', headers,
        body: JSON.stringify({
          message: msg,
          session_id: sid,
          lang,
          sections,
          loop_region: loopRegion ? { start_pct: loopRegion.startPct, end_pct: loopRegion.endPct } : null,
          daw_context: dawContext,
          locked_stems: lockedStems,
          vocal_locked: vocalLocked,
        }),
      });
      const data = await r.json();
      if (r.ok) {
        setMessages(prev => [...prev, { role: 'assistant', content: data.response, timestamp: new Date().toISOString() }]);
        if (data.stages) setStages(data.stages);
        if (data.chips && data.chips.length > 0) setChips(data.chips);

        // Handle low balance warning
        if (data.low_balance) {
          toast.warning(lang === 'es' ? 'Saldo bajo. Recarga corcheas para seguir creando.' : 'Low balance. Top up to keep creating.', { duration: 5000 });
        }

        // Auto-trigger generation if brain decided
        if (data.task_id) {
          setTaskId(data.task_id);
          setGenerating(data.action === 'iterate' ? 'iterate' : 'base');
          startGenTimer();
          const genMsg = lang === 'es'
            ? (data.action === 'iterate' ? 'Aplicando cambios...' : 'Creando tu track...')
            : (data.action === 'iterate' ? 'Applying changes...' : 'Creating your track...');
          toast.info(genMsg, { duration: 8000 });
        }

        // Auto-trigger stem separation if brain decided
        if (data.action === 'separate_stems' && currentAudio) {
          toast.info(lang === 'es' ? 'Separando instrumentos...' : 'Separating instruments...', { duration: 10000 });
        }

        // Apply structure update from PULSE
        if (data.structure_update && data.structure_update.length >= 2) {
          const barLength = (60 / bpm) * 4;
          const SECTION_BARS = { intro: 8, verse: 16, prechorus: 8, drop: 16, breakdown: 8, outro: 8 };
          let t = 0;
          const newSections = data.structure_update.map(type => {
            const bars = SECTION_BARS[type] || 8;
            const dur = bars * barLength;
            const s = { type, bars, startTime: t, endTime: t + dur };
            t += dur;
            return s;
          });
          setSections(newSections);
        }
      }
    } catch {
      setMessages(prev => [...prev, { role: 'assistant', content: lang === 'es' ? 'Error de conexion. Intenta de nuevo.' : 'Connection error. Try again.', timestamp: new Date().toISOString(), error: true }]);
      toast.error(lang === 'es' ? 'Error de conexion con PULSE' : 'Connection error with PULSE');
    } finally { setSending(false); }
  }, [input, sending, sessionId, lang, headers, sections, bpm, startGenTimer, loopRegion, dawContext, lockedStems, vocalLocked]);

  // When a structure change is made via quick buttons, notify PULSE
  const handleStructureChat = useCallback((desc) => {
    if (!sessionIdRef.current) return;
    sendMessage(`[estructura] ${desc}`);
  }, [sendMessage]);

  const generateStep = async (step) => {
    if (!legalAI.gate()) return;
    const sid = sessionIdRef.current || sessionId;
    if (!sid) { toast.error('Sesión no iniciada'); return; }
    const cost = STEPS.find(s => s.key === step)?.cost || 0;
    if (wallet.balance < cost) { toast.error(`Necesitas ${cost} corcheas`); setShowPackModal(true); return; }
    setGenerating(step);
    try {
      const r = await fetch(`${API}/api/producer/generate/${step}`, {
        method: 'POST', headers,
        body: JSON.stringify({
          session_id: sid,
          step,
          sections: sections.map(s => ({ type: s.type, bars: s.bars })),
          loop_region: loopRegion ? { start_pct: loopRegion.startPct, end_pct: loopRegion.endPct } : null,
          locked_stems: lockedStems,
          vocal_locked: vocalLocked,
        }),
      });
      const data = await r.json();
      if (r.ok) {
        setStages(data.stages);
        setWallet(prev => ({ ...prev, balance: data.new_balance }));
        const result = data.result;
        setProject(prev => ({ ...prev, [step]: result, title: result.title || prev.title }));
        if (step === 'base') {
          if (result.bpm) setBpm(result.bpm);
          if (result.key) setMusicalKey(result.key.replace(' major', '').replace(' minor', 'm'));
          setPublishTitle(result.title || '');
        }
        if (data.task_id) {
          // Check if audio is already available (sync mode)
          if (data.audio_url) {
            setCurrentAudio(data.audio_url);
            if (data.generation_mode) setMusicProvider(data.generation_mode);
            toast.success(`Audio generado con ${data.generation_mode || 'IA'}`, { duration: 5000 });
          } else {
            setTaskId(data.task_id);
            if (data.generation_mode) setMusicProvider(data.generation_mode);
            toast.info('Generando musica... puede tardar 1-2 min', { duration: 8000 });
          }
        } else if (result.audio_b64) {
          const blob = new Blob([Uint8Array.from(atob(result.audio_b64), c => c.charCodeAt(0))], { type: 'audio/mp3' });
          const url = URL.createObjectURL(blob);
          setCurrentAudio(url);
          toast.success('Audio generado.');
        }
        // Show provider errors as actionable warnings
        if (result.music_error) {
          toast.warning(result.music_error, { duration: 10000 });
        }
        if (result.voice_error) {
          toast.warning(result.voice_error, { duration: 10000 });
        }
        let resultMsg = '';
        if (step === 'base') {
          const mode = data.generation_mode;
          const indicator = mode !== 'brief' ? `Generando con ${mode}...` : 'Brief musical generado';
          resultMsg = `**${indicator}**\n\n**${result.title || 'Tu cancion'}** — ${result.bpm || bpm} BPM · ${result.key || musicalKey} · ${result.style || ''}`;
          if (result.music_error) {
            resultMsg += `\n\n_${result.music_error}_`;
          }
        } else if (step === 'voice') {
          const mode = data.generation_mode;
          const indicator = mode === 'fish_audio' ? 'Voz sintetizada' : 'Brief vocal';
          resultMsg = `**${indicator}**\n\n${result.lyrics_snippet ? `_${result.lyrics_snippet}_` : ''}\n\n${result.voice_description || ''}`;
          if (result.voice_error) {
            resultMsg += `\n\n_${result.voice_error}_`;
          }
        } else if (step === 'improve') {
          resultMsg = `**Mejoras aplicadas**\n\n${result.final_description || ''}`;
          if (result.music_error) {
            resultMsg += `\n\n_${result.music_error}_`;
          }
        }
        setMessages(prev => [...prev, { role: 'assistant', content: resultMsg || 'Paso completado', timestamp: new Date().toISOString(), isGeneration: true, step }]);
        toast.success(`${STEPS.find(s => s.key === step)?.name} completada. -${cost} corcheas`);
        fetchVersions();
      } else {
        toast.error(data.detail || 'Error en la generación');
      }
    } catch { toast.error('Error de conexión'); }
    finally { setGenerating(null); }
  };

  const getNextStep = useCallback(() => {
    if (!stages.base_done) return 'base';
    if (!stages.voice_done) return 'voice';
    if (!stages.improved) return 'improve';
    return 'improve';
  }, [stages]);

  const generateMultiSamples = async ({ numSamples }) => {
    if (!legalAI.gate()) return;
    const step = getNextStep();
    const sid = sessionIdRef.current || sessionId;
    if (!sid) return;
    setGenerating(step);
    try {
      const r = await fetch(`${API}/api/producer/generate/multi/${step}`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          session_id: sid,
          num_samples: numSamples,
        }),
      });
      const data = await r.json();
      if (!r.ok) {
        toast.error(data.detail || 'No se pudo generar variaciones');
        return;
      }
      setWallet(prev => ({ ...prev, balance: data.new_balance }));
      setStages(data.stages || stages);
      setSampleGroup({
        groupId: data.group_id,
        step,
        samples: (data.task_ids || []).map((id, idx) => ({
          task_id: id,
          status: 'generating',
          sample_index: idx,
          audio_url: null,
        })),
      });
      toast.success(`Generando ${numSamples} variaciones para ${step}`);
    } catch {
      toast.error('Error lanzando variaciones');
    } finally {
      setGenerating(null);
    }
  };

  const pollSampleGroup = useCallback(async (groupId = null) => {
    const targetGroupId = groupId || sampleGroup?.groupId;
    if (!targetGroupId || !token) return;
    try {
      const r = await fetch(`${API}/api/producer/task/group/${targetGroupId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!r.ok) return;
      const data = await r.json();
      const mapped = (data.samples || []).map(s => ({
        task_id: s.task_id,
        status: s.status,
        audio_url: s.audio_url,
        sample_index: s.sample_index,
      }));
      setSampleGroup(prev => prev ? { ...prev, samples: mapped } : prev);
    } catch {}
  }, [sampleGroup?.groupId, token]);

  const selectSample = async (taskId) => {
    const sid = sessionIdRef.current || sessionId;
    if (!sid || !taskId) return;
    try {
      const r = await fetch(`${API}/api/producer/select-sample`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ task_id: taskId, session_id: sid }),
      });
      const data = await r.json();
      if (!r.ok) {
        toast.error(data.detail || 'No se pudo seleccionar la muestra');
        return;
      }
      if (data.audio_url) {
        setCurrentAudio(data.audio_url);
        setPreviewState({ enabled: false, mainAudio: null });
      }
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: `Muestra seleccionada. La sesión ahora usa esta versión en **${data.step || 'base'}**.`,
        timestamp: new Date().toISOString(),
      }]);
      fetchVersions(sid);
      toast.success('Variación aplicada al proyecto');
    } catch {
      toast.error('Error aplicando la variación');
    }
  };

  const handlePreviewToggle = useCallback((audioUrl, enabled) => {
    if (enabled && audioUrl) {
      setPreviewState(prev => ({ enabled: true, mainAudio: prev.mainAudio || currentAudio }));
      setCurrentAudio(audioUrl);
      setSeekRequest(playbackTime);
      return;
    }
    setPreviewState(prev => {
      if (prev.mainAudio) {
        setCurrentAudio(prev.mainAudio);
        setSeekRequest(playbackTime);
      }
      return { enabled: false, mainAudio: null };
    });
  }, [currentAudio, playbackTime]);

  const claimAdReward = async () => {
    const today = new Date().toISOString().split('T')[0];
    const claimed = wallet.daily_ads_date === today ? wallet.daily_ads_claimed : 0;
    if (claimed >= 5) { toast.error('Ya viste 5 anuncios hoy'); return; }
    setClaimingAd(true);
    toast.info('Reproduciendo anuncio...', { duration: 3000 });
    await new Promise(r => setTimeout(r, 3100));
    try {
      const r = await fetch(`${API}/api/producer/wallet/claim-ad`, { method: 'POST', headers });
      const data = await r.json();
      if (r.ok) {
        setWallet(prev => ({ ...prev, balance: data.new_balance, daily_ads_claimed: data.ads_today, daily_ads_date: today }));
        setCorcheaAnim({ amount: 4, key: `ad-${Date.now()}` });
      } else { toast.error(data.detail); }
    } catch { toast.error('Error'); }
    finally { setClaimingAd(false); }
  };

  const publishProject = async () => {
    if (!publishTitle.trim()) { toast.error('Pon un título'); return; }
    if (!legalAccepted) { toast.error('Acepta los términos para publicar'); return; }
    setPublishing(true);
    try {
      await fetch(`${API}/api/producer/legal/accept`, {
        method: 'POST', headers,
        body: JSON.stringify({ content_type: 'producer_publish', version: '1.0' }),
      });
      const r = await fetch(`${API}/api/producer/publish`, {
        method: 'POST', headers,
        body: JSON.stringify({ project_id: sessionIdRef.current || sessionId, title: publishTitle }),
      });
      const data = await r.json();
      if (r.ok) {
        setStages(p => ({ ...p, published: true }));
        setWallet(p => ({ ...p, balance: data.new_balance }));
        setPublishedSongId(data.song_id);
        setCorcheaAnim({ amount: data.corcheas_earned, key: `pub-${Date.now()}` });
        toast.success(data.message, { duration: 5000 });
        setMessages(p => [...p, { role: 'assistant', content: `**Publicada.** "${data.title}" ya está en PulseMusic. +${data.corcheas_earned} corcheas.`, timestamp: new Date().toISOString(), isPublish: true }]);
      } else { toast.error(data.detail); }
    } catch { toast.error('Error publicando'); }
    finally { setPublishing(false); }
  };

  const iterateProject = async (instruction) => {
    if (!legalAI.gate()) return;
    if (!stages.base_done) { toast.error('Genera la base primero'); return; }
    if (wallet.balance < 3) { toast.error('Necesitas 3 corcheas para iterar'); setShowPackModal(true); return; }
    setGenerating('iterate');
    try {
      const lastUserMsg = instruction || messages.filter(m => m.role === 'user').pop()?.content || '';
      const r = await fetch(`${API}/api/producer/iterate`, {
        method: 'POST', headers,
        body: JSON.stringify({
          session_id: sessionIdRef.current || sessionId,
          instruction: lastUserMsg,
          sections: sections.map(s => ({ type: s.type, bars: s.bars })),
          loop_region: loopRegion ? { start_pct: loopRegion.startPct, end_pct: loopRegion.endPct } : null,
          locked_stems: lockedStems,
          vocal_locked: vocalLocked,
        }),
      });
      const data = await r.json();
      if (r.ok) {
        setWallet(prev => ({ ...prev, balance: data.new_balance }));
        setVersion(data.version || version + 1);
        if (data.task_id) {
          setTaskId(data.task_id);
          toast.info('Re-generando con tus cambios... puede tardar 1-2 min', { duration: 8000 });
        }
        if (data.updated_brief) {
          setProject(prev => ({
            ...prev,
            base: { ...prev.base, ...data.updated_brief },
            title: data.updated_brief.title || prev.title,
          }));
          setPublishTitle(data.updated_brief.title || publishTitle);
          if (data.updated_brief.bpm) setBpm(data.updated_brief.bpm);
          if (data.updated_brief.key) setMusicalKey(data.updated_brief.key.replace(' major', '').replace(' minor', 'm'));
        }
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: `**Iteración v${data.version}** — ${data.iteration_note || 'Modificaciones aplicadas'}.\n\n${data.generation_mode === 'async' ? 'Re-generando audio...' : 'Brief actualizado.'}`,
          timestamp: new Date().toISOString(),
          isIteration: true,
        }]);
        toast.success(`Iteración aplicada. -3 corcheas`);
        fetchVersions();
      } else { toast.error(data.detail || 'Error en la iteración'); }
    } catch { toast.error('Error de conexión'); }
    finally { setGenerating(null); }
  };

  // ── Intent Engine handler ──
  const handleIntent = async (instruction) => {
    if (!instruction) return;
    await iterateProject(instruction);
  };

  // ── Auto-save DAW state (debounced 3s) ──
  const saveTimerRef = useRef(null);
  useEffect(() => {
    const sid = sessionIdRef.current || sessionId;
    if (!sid || !token || !stages.base_done) return;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        await fetch(`${API}/api/producer/sessions/${sid}/state`, {
          method: 'PUT', headers,
          body: JSON.stringify({
            sections: sections.map(s => ({ type: s.type, bars: s.bars })),
            loop_region: loopRegion ? { start_pct: loopRegion.startPct, end_pct: loopRegion.endPct } : null,
            locked_stems: lockedStems,
            vocal_locked: vocalLocked,
          }),
        });
      } catch {}
    }, 3000);
    return () => { if (saveTimerRef.current) clearTimeout(saveTimerRef.current); };
  }, [sections, loopRegion, stages.base_done, lockedStems, vocalLocked]);

  const today = new Date().toISOString().split('T')[0];
  const adsToday = wallet.daily_ads_date === today ? wallet.daily_ads_claimed : 0;
  const adsLeft = 5 - adsToday;
  const projectBpm = project?.base?.bpm || bpm;
  const projectKey = project?.base?.key?.replace(' major', '').replace(' minor', 'm') || musicalKey;
  const projectEnergy = project?.base?.energy || 7;

  // ── Entry Flow Handlers ──
  const handleCreateMusic = async (styleMsg, styleName = '') => {
    setStudioMode('create');
    pendingMsgRef.current = styleMsg;
    const sid = await initSession(styleName);
    if (!sid) {
      pendingMsgRef.current = null;
      return;
    }
    setPhase('studio');
  };

  const handleImproveMusic = () => {
    setPhase('upload');
  };

  const handleUploadComplete = ({ audioUrl, scanResult, fileName, sessionId: uploadSessionId, referenceAudioUrl, mode }) => {
    setStudioMode('improve');
    setPhase('studio');
    const id = uploadSessionId || `sess_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    setSessionId(id);
    sessionIdRef.current = id;
    setCurrentAudio(audioUrl);
    setStages({ base_done: true, voice_done: false, improved: false, published: false });
    setProject({ title: fileName.replace(/\.\w+$/, ''), base: { bpm: 120, key: 'Am', style: 'original', energy: 7 } });
    initSections('default', 180);
    setPublishTitle(fileName.replace(/\.\w+$/, ''));
    const flagNote = scanResult?.flagged
      ? '\nNota: No se pudo verificar los derechos del audio. Asegurate de tener los derechos necesarios.'
      : '';
    setMessages([{
      role: 'assistant',
      content: `Tu cancion esta cargada y lista en pista. La IA trabajara SOBRE tu audio:\n\n- Pide cambios de estilo, instrumentos o mood\n- Ajusta BPM, key y efectos\n- Genera mejoras que se aplican a tu audio original${flagNote}`,
      timestamp: new Date().toISOString(),
    }]);
  };

  const handleBackToEntry = () => {
    setPhase('entry');
    setMessages([]);
    setStages({ base_done: false, voice_done: false, improved: false, published: false });
    setSections([]);
    setDefaultSections([]);
    setProject({});
    setCurrentAudio(null);
    fetchProjects();
  };

  // ── Session Resume ──
  const handleResumeSession = async (session) => {
    const sid = session.session_id;
    // Load full session from server if messages are not included
    let fullSession = session;
    if (!session.messages || session.messages.length === 0) {
      try {
        const r = await fetch(`${API}/api/producer/sessions/${sid}`, { headers });
        if (r.ok) {
          fullSession = await r.json();
        }
      } catch {}
    }
    setSessionId(sid);
    sessionIdRef.current = sid;
    setPhase('studio');
    setStudioMode(fullSession.mode || 'create');
    setStages(fullSession.stages || { base_done: false, voice_done: false, improved: false, published: false });
    setProject(fullSession.project || {});
    setMessages(fullSession.messages || []);
    setVersion(fullSession.versions?.length || 1);
    // Restore structure
    if (session.sections?.length) {
      const restored = session.sections.map(s => ({ ...s, id: `${s.type}_${Math.random().toString(36).slice(2, 8)}` }));
      setSections(restored);
      setDefaultSections(restored);
    } else {
      const style = session.project?.base?.style?.toLowerCase() || 'default';
      const dur = session.project?.base?.duration_seconds || 180;
      initSections(style, dur);
    }
    // Restore loop
    if (session.loop_region) setLoopRegion(session.loop_region);
    if (fullSession.locked_stems && typeof fullSession.locked_stems === 'object') {
      setLockedStems((prev) => ({ ...prev, ...fullSession.locked_stems }));
    }
    if (typeof fullSession.vocal_locked === 'boolean') {
      setVocalLocked(fullSession.vocal_locked);
    }
    // Restore audio
    const audio = session.project?.improve?.audio_url || session.project?.voice?.audio_url || session.project?.base?.audio_url || null;
    if (audio) setCurrentAudio(audio);
    // Restore BPM/Key
    if (session.project?.base?.bpm) setBpm(session.project.base.bpm);
    if (session.project?.base?.key) setMusicalKey(session.project.base.key.replace(' major', '').replace(' minor', 'm'));
    if (session.project?.title) setPublishTitle(session.project.title);
    // Load versions
    fetchVersions(sid);
  };

  // ── ENTRY SCREEN ──
  if (phase === 'entry') {
    return <ProducerEntryScreen onCreateMusic={handleCreateMusic} onImproveMusic={handleImproveMusic} onResumeSession={handleResumeSession} balance={wallet.balance} token={token} projects={projectList} onNewProject={async () => { setStudioMode('create'); pendingMsgRef.current = null; const sid = await initSession(); if (sid) setPhase('studio'); }} />;
  }

  // ── UPLOAD FLOW ──
  if (phase === 'upload') {
    return <ProducerUploadFlow onComplete={handleUploadComplete} onBack={() => setPhase('entry')} token={token} />;
  }

  const isProducerUnlock =
    user?.email?.toLowerCase().trim() === PRODUCER_UNLOCK_EMAIL;

  // ── STUDIO: "PRÓXIMAMENTE" only after PulseProducer onboarding tour, only for non-owner ──
  if (phase === 'studio' && !isProducerUnlock && studioTourDone) {
    return <ProducerComingSoonScreen onBack={() => navigate('/')} />;
  }

  // ── STUDIO ──
  return (
    <div className="fixed inset-0 flex flex-col bg-[#0A0A0A] text-white overflow-hidden producer-noise z-[60]" data-testid="producer-studio">
      {/* Studio Onboarding (how to use PulseProducer — before PRÓXIMAMENTE for guests) */}
      <StudioOnboarding
        visible={!stages.base_done}
        onProducerTourFinished={() => setStudioTourDone(true)}
      />
      {/* Legal acceptance modal */}
      {legalAI.legalModal}
      {/* Corchea floating animation */}
      {corcheaAnim && <CorcheaAnimation amount={corcheaAnim.amount} triggerKey={corcheaAnim.key} />}

      {/* ── Top Bar ── */}
      <div className="flex-shrink-0 flex items-center justify-between px-2.5 py-1.5 border-b border-[#1A1A1F] bg-[#0A0A0A]/95 backdrop-blur z-20">
        <div className="flex items-center gap-2">
          <button
            onClick={handleBackToEntry}
            className="w-6 h-6 rounded-md flex items-center justify-center text-[#52525B] hover:text-white hover:bg-[#1A1A1F] transition-all"
            data-testid="back-to-entry"
          >
            <ChevronLeft size={14} />
          </button>
          <div className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0" style={{ background: 'linear-gradient(135deg, #F5C842, #FF6B00)' }}>
            <Music2 size={10} className="text-black" />
          </div>
          <span className="text-[11px] font-bold text-[#71717A] tracking-wider uppercase hidden sm:block">Pulse</span>
        </div>

        {/* Minimal PULSE badge — no wallet */}
        <div className="flex items-center gap-2">
          <button data-testid="recharge-top-btn" onClick={() => setShowPackModal(true)}
            className="text-[9px] px-2 py-1 rounded-md bg-[#F5C84210] text-[#F5C842] border border-[#F5C84020] hover:bg-[#F5C84220] transition-colors font-bold">
            + Recargar
          </button>
        </div>

        {/* Publish + New session */}
        <div className="flex items-center gap-2">
          {stages.base_done && (
            <button
              data-testid="publish-btn"
              onClick={() => setShowPublishModal(true)}
              className="flex items-center gap-1 px-2.5 py-1 rounded-md text-[9px] font-bold transition-all hover:scale-105"
              style={{ background: 'linear-gradient(135deg, #00D4FF, #0891B2)', color: '#000' }}
            >
              <Zap size={10} />
              <span>{lang === 'es' ? 'Publicar' : 'Publish'}</span>
            </button>
          )}
          <button
            data-testid="new-session-btn"
            onClick={handleBackToEntry}
            className="flex items-center gap-1 px-1.5 py-1 rounded-md border border-[#1A1A1F] text-[9px] text-[#52525B] hover:border-[#F5C84030] hover:text-[#F5C842] transition-colors"
          >
            <RefreshCw size={10} /> <span className="hidden sm:inline">{lang === 'es' ? 'Nuevo' : 'New'}</span>
          </button>
        </div>
      </div>
      {backendReachable === false && (
        <div className="flex-shrink-0 mx-2 mt-1 rounded-lg border border-red-500/40 bg-red-500/10 px-2 py-1.5 text-[10px] text-red-200">
          {lang === 'es'
            ? 'Ahora mismo no podemos conectar con el estudio. Prueba en unos minutos o recarga la página.'
            : 'We can’t reach the studio right now. Try again in a few minutes or refresh the page.'}
        </div>
      )}
      {backendReachable !== false && providerStatus.loaded && !providerStatus.hasSuno && (
        <div className="flex-shrink-0 mx-2 mt-1 rounded-lg border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-[10px] text-amber-100">
          {lang === 'es'
            ? 'El chat sigue disponible; la generación de música en el estudio está en pausa hasta que el servicio esté listo.'
            : 'Chat still works; music generation in the studio is paused until the service is ready.'}
        </div>
      )}

      {/* ── Main Content ── */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {/* Desktop layout */}
        <div className="hidden lg:flex h-full min-h-0">
          {/* ── PROJECT SIDEBAR (desktop) ── */}
          <div className={`flex flex-col flex-shrink-0 border-r border-[#1A1A1F] bg-[#07070A] overflow-hidden min-h-0 transition-all duration-200 ${sidebarOpen ? 'w-[168px]' : 'w-[40px]'}`}>
            <div className="flex items-center justify-between px-2 py-2 border-b border-[#1A1A1F]">
              {sidebarOpen && <span className="text-[9px] font-bold text-[#52525B] uppercase tracking-wider">{lang === 'es' ? 'Proyectos' : 'Projects'}</span>}
              <button
                data-testid="toggle-sidebar-btn"
                onClick={() => setSidebarOpen(p => !p)}
                className="w-6 h-6 rounded flex items-center justify-center text-[#52525B] hover:text-white hover:bg-[#1A1A1F] transition-all"
              >
                <FolderOpen size={12} />
              </button>
            </div>
            <button
              data-testid="new-project-sidebar-btn"
              onClick={async () => {
                setStudioMode('create');
                pendingMsgRef.current = null;
                await initSession();
              }}
              className="mx-2 mt-2 mb-1 flex items-center gap-1.5 px-2 py-1.5 rounded-lg border border-dashed border-[#F5C84030] text-[9px] text-[#F5C842] hover:bg-[#F5C84010] transition-colors"
            >
              <Plus size={10} />
              {sidebarOpen && <span>{lang === 'es' ? 'Nuevo proyecto' : 'New project'}</span>}
            </button>
            {sidebarOpen && (
              <div className="flex-1 overflow-y-auto px-1 py-1 space-y-0.5 scrollbar-thin">
                {projectList.map((p) => {
                  const isActive = (sessionIdRef.current || sessionId) === p.session_id;
                  const d = p.display || {};
                  const title = d.title || (lang === 'es' ? 'Sin título' : 'Untitled');
                  const statusColor = d.status === 'published' ? '#00D4FF' : d.status === 'produced' ? '#F5C842' : '#52525B';
                  return (
                    <button
                      key={p.session_id}
                      data-testid={`project-item-${p.session_id}`}
                      onClick={() => handleResumeSession(p)}
                      className={`w-full text-left px-2 py-1.5 rounded-md text-[10px] transition-all group ${
                        isActive ? 'bg-[#F5C84015] border border-[#F5C84030]' : 'hover:bg-[#1A1A1F] border border-transparent'
                      }`}
                    >
                      <div className="flex items-center gap-1.5">
                        <div className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: statusColor }} />
                        <span className="truncate text-[#A1A1AA] group-hover:text-white font-medium">{title}</span>
                      </div>
                      {d.style && <div className="text-[8px] text-[#52525B] truncate mt-0.5 ml-3">{d.style}</div>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Desktop chat */}
          <div className="flex flex-col w-[248px] xl:w-[284px] flex-shrink-0 border-r border-[#1A1A1F] bg-[#0A0A0A] overflow-hidden min-h-0 p-2 pt-2">
            <ChatColumn
              messages={messages}
              sending={sending}
              input={input}
              setInput={setInput}
              onSend={sendMessage}
              stages={stages}
              lang={lang}
              user={user}
              messagesEndRef={messagesEndRef}
              sessionId={sessionIdRef.current || sessionId}
              sections={sections}
              loopRegion={loopRegion}
              onExecuteAction={iterateProject}
              token={token}
              chips={chips}
              genTimer={genTimer}
              genMessage={genMessage}
              taskPolling={taskPolling}
            />
          </div>

          {/* Desktop workbench center */}
          <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
            <div className="flex-shrink-0 px-2.5 pt-1.5 pb-1.5 border-b border-[#1A1A1F] bg-[#0B0B0D] space-y-1.5">
              <IntentEngine
                sessionId={sessionIdRef.current || sessionId}
                sections={sections}
                loopRegion={loopRegion}
                stages={stages}
                token={token}
                onExecute={handleIntent}
              />
              <GenerationControls
                step={getNextStep()}
                balance={wallet.balance}
                disabled={!!generating || taskPolling}
                onGenerate={generateMultiSamples}
              />
              {!!sampleGroup && (
                <SamplePicker
                  groupId={sampleGroup.groupId}
                  samples={sampleGroup.samples || []}
                  onSelect={selectSample}
                  onPollGroup={pollSampleGroup}
                />
              )}
            </div>
            <div className="flex-1 min-h-0 overflow-hidden p-2 lg:p-2.5">
              <ProducerDAWView
                audioUrl={currentAudio}
                generating={generating}
                taskPolling={taskPolling}
                genTimer={genTimer}
                genMessage={genMessage}
                stages={stages}
                sessionId={sessionIdRef.current || sessionId}
                token={token}
                playerRef={playerRef}
                loopRegion={loopRegion}
                sections={sections}
                defaultSections={defaultSections}
                bpm={bpm}
                onSectionsChange={(newSections) => setSections(newSections)}
                onLoopChange={(lr) => setLoopRegion(lr)}
                onChatMessage={handleStructureChat}
                onSeekRequest={seekRequest}
                onAutoPlayPreview={previewState.enabled}
                onPlaybackTimeChange={setPlaybackTime}
                onPublish={() => {
                  if (!publishTitle.trim()) setPublishTitle(`${project?.title || 'Mi cancion'} — ft Pulse`);
                  setPublishedSongId(null);
                  setLegalAccepted(false);
                  setShowPublishModal(true);
                }}
                lockedStems={lockedStems}
                onLockedStemsChange={setLockedStems}
                vocalLocked={vocalLocked}
                onVocalLockedChange={setVocalLocked}
                className="h-full"
              />
            </div>
            <div className="flex-shrink-0 px-2.5 py-1.5 border-t border-[#1A1A1F] bg-[#0B0B0D]">
              <VocalRecorder
                sessionId={sessionIdRef.current || sessionId}
                token={token}
                onVocalReady={(url) => {
                  setStages(prev => ({ ...prev, voice_done: true }));
                  if (url) setCurrentAudio(url);
                  toast.success(lang === 'es' ? 'Vocal grabada y lista' : 'Vocal recorded and ready');
                }}
                onInspireResult={(data) => {
                  if (data.task_id) {
                    setTaskId(data.task_id);
                    setGenerating('base');
                    startGenTimer();
                  }
                }}
              />
            </div>
          </div>

          {/* Desktop right panel */}
          <div className="flex flex-col w-[236px] xl:w-[264px] flex-shrink-0 border-l border-[#1A1A1F] p-2.5 gap-2.5 overflow-y-auto producer-scroll bg-[#0A0A0A]">
            <WalletMini
              wallet={wallet}
              adsToday={adsToday}
              adsLeft={adsLeft}
              claimingAd={claimingAd}
              onClaimAd={claimAdReward}
              onBuy={() => setShowPackModal(true)}
            />
            {versions.length > 0 && (
              <VersionTimeline
                versions={versions}
                currentVersion={version}
                onRestore={(idx) => restoreVersion(idx)}
                onPreview={(url, enabled) => handlePreviewToggle(url, enabled)}
              />
            )}
            <ProducerDJPanel
              bpm={projectBpm}
              musicalKey={projectKey}
              energy={projectEnergy}
              onBpmChange={setBpm}
              onKeyChange={setMusicalKey}
              onEQChange={handleEQChange}
              onEffectChange={(effect, active) => {
                const p = playerRef.current;
                if (!p) return;
                if (effect === 'reverb') p.setReverb?.(active ? 5 : 0);
                if (effect === 'delay') p.setDelay?.(active ? 4 : 0);
                if (effect === 'filter') p.setFilter?.(active ? 8 : 20);
                if (effect === 'loop') {
                  if (active && loopRegion) p.seekToLoopStart?.();
                }
                if (effect === 'sidechain') {
                  p.setTrackGain?.('drums', active ? 3 : 0);
                  p.setTrackGain?.('bass', active ? -6 : 0);
                }
              }}
              disabled={!stages.base_done}
              className="flex-1"
            />
          </div>
        </div>

        {/* Mobile layout */}
        <div className="lg:hidden h-full min-h-0 flex flex-col">
          <div className="flex border-b border-[#1A1A1F] bg-[#0A0A0A]">
            {[['chat', 'Chat'], ['daw', 'DAW'], ['dj', 'DJ']].map(([tab, label]) => (
              <button
                key={tab}
                data-testid={`producer-tab-${tab}`}
                onClick={() => setMobileTab(tab)}
                className={`flex-1 py-1.5 text-[9px] font-bold tracking-wider uppercase transition-colors ${
                  mobileTab === tab ? 'text-[#F5C842] border-b-2 border-[#F5C842]' : 'text-[#3F3F46]'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          <div className="flex-1 min-h-0 overflow-hidden">
            {mobileTab === 'chat' && (
              <div className="h-full flex flex-col px-1.5 py-1.5">
                <ChatColumn
                  messages={messages}
                  sending={sending}
                  input={input}
                  setInput={setInput}
                  onSend={sendMessage}
                  stages={stages}
                  lang={lang}
                  user={user}
                  messagesEndRef={messagesEndRef}
                  sessionId={sessionIdRef.current || sessionId}
                  sections={sections}
                  loopRegion={loopRegion}
                  onExecuteAction={iterateProject}
                  token={token}
                  chips={chips}
                  genTimer={genTimer}
                  genMessage={genMessage}
                  taskPolling={taskPolling}
                  compactMobile
                />
              </div>
            )}
            {mobileTab === 'daw' && (
              <div className="h-full flex flex-col min-h-0">
                <div className="flex-shrink-0 px-1.5 py-1.5 border-b border-[#1A1A1F] bg-[#0B0B0D] space-y-1.5">
                  <IntentEngine
                    sessionId={sessionIdRef.current || sessionId}
                    sections={sections}
                    loopRegion={loopRegion}
                    stages={stages}
                    token={token}
                    onExecute={handleIntent}
                  />
                  <GenerationControls
                    step={getNextStep()}
                    balance={wallet.balance}
                    disabled={!!generating || taskPolling}
                    onGenerate={generateMultiSamples}
                  />
                  {!!sampleGroup && (
                    <SamplePicker
                      groupId={sampleGroup.groupId}
                      samples={sampleGroup.samples || []}
                      onSelect={selectSample}
                      onPollGroup={pollSampleGroup}
                    />
                  )}
                </div>
                <div className={`${mobileDawHasPrimaryContent ? 'flex-1 min-h-0' : 'flex-none'} overflow-hidden px-1.5 py-1.5`}>
                  <ProducerDAWView
                    audioUrl={currentAudio}
                    generating={generating}
                    taskPolling={taskPolling}
                    genTimer={genTimer}
                    genMessage={genMessage}
                    stages={stages}
                    sessionId={sessionIdRef.current || sessionId}
                    token={token}
                    playerRef={playerRef}
                    loopRegion={loopRegion}
                    sections={sections}
                    defaultSections={defaultSections}
                    bpm={bpm}
                    onSectionsChange={(newSections) => setSections(newSections)}
                    onLoopChange={(lr) => setLoopRegion(lr)}
                    onChatMessage={handleStructureChat}
                    onSeekRequest={seekRequest}
                    onAutoPlayPreview={previewState.enabled}
                    onPlaybackTimeChange={setPlaybackTime}
                    onPublish={() => setShowPublishModal(true)}
                    lockedStems={lockedStems}
                    onLockedStemsChange={setLockedStems}
                    vocalLocked={vocalLocked}
                    onVocalLockedChange={setVocalLocked}
                    className={mobileDawHasPrimaryContent ? 'h-full' : 'h-auto'}
                    compactEmptyState={!mobileDawHasPrimaryContent}
                  />
                </div>
                <div className="flex-shrink-0 px-1.5 py-1.5 border-t border-[#1A1A1F] bg-[#0B0B0D]">
                  <VocalRecorder
                    sessionId={sessionIdRef.current || sessionId}
                    token={token}
                    onVocalReady={(url) => {
                      setStages(prev => ({ ...prev, voice_done: true }));
                      if (url) setCurrentAudio(url);
                    }}
                    onInspireResult={(data) => {
                      if (data.task_id) {
                        setTaskId(data.task_id);
                        setGenerating('base');
                        startGenTimer();
                      }
                    }}
                  />
                </div>
              </div>
            )}
            {mobileTab === 'dj' && (
              <div className="h-full overflow-y-auto producer-scroll px-2 py-1.5 space-y-2 relative">
                <div className="flex justify-end pointer-events-none">
                  <div className="pointer-events-auto mb-1">
                    <StudioCompanionBubble visible stage="dj" />
                  </div>
                </div>
                <WalletMini
                  wallet={wallet}
                  adsToday={adsToday}
                  adsLeft={adsLeft}
                  claimingAd={claimingAd}
                  onClaimAd={claimAdReward}
                  onBuy={() => setShowPackModal(true)}
                />
                {versions.length > 0 && (
                  <VersionTimeline
                    versions={versions}
                    currentVersion={version}
                    onRestore={(idx) => restoreVersion(idx)}
                    onPreview={(url, enabled) => handlePreviewToggle(url, enabled)}
                  />
                )}
                <ProducerDJPanel
                  bpm={projectBpm}
                  musicalKey={projectKey}
                  energy={projectEnergy}
                  onBpmChange={setBpm}
                  onKeyChange={setMusicalKey}
                  onEQChange={handleEQChange}
                  onEffectChange={(effect, active) => {
                    const p = playerRef.current;
                    if (!p) return;
                    if (effect === 'reverb') p.setReverb?.(active ? 5 : 0);
                    if (effect === 'delay') p.setDelay?.(active ? 4 : 0);
                    if (effect === 'filter') p.setFilter?.(active ? 8 : 20);
                    if (effect === 'loop' && active && loopRegion) p.seekToLoopStart?.();
                    if (effect === 'sidechain') {
                      p.setTrackGain?.('drums', active ? 3 : 0);
                      p.setTrackGain?.('bass', active ? -6 : 0);
                    }
                  }}
                  disabled={!stages.base_done}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Pack purchase modal ── */}
      {showPackModal && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/80 backdrop-blur-sm px-4">
          <div className="bg-[#0F0F12] border border-[#1A1A1F] rounded-2xl p-5 max-w-sm w-full" data-testid="pack-purchase-modal">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-bold text-white">Recargar Corcheas</h2>
              <button onClick={() => setShowPackModal(false)}><X size={16} className="text-[#52525B] hover:text-white transition-colors" /></button>
            </div>
            <div className="grid grid-cols-2 gap-2.5 mb-4">
              {packs.map(p => (
                <button key={p.id} data-testid={`pack-${p.id}`}
                  onClick={() => { setShowPackModal(false); navigate('/wallet'); }}
                  className={`p-3.5 rounded-xl border text-left transition-all hover:scale-[1.02] active:scale-[0.98] ${
                    p.popular ? 'border-[#F5C84260] bg-[#F5C84208]' : 'border-[#1A1A1F] bg-[#0A0A0B]'
                  }`}>
                  {p.popular && <span className="text-[8px] font-bold text-[#F5C842] tracking-wider block mb-1">POPULAR</span>}
                  <div className="text-lg font-bold text-[#F5C842]">{p.corcheas}</div>
                  <div className="text-[10px] text-[#71717A]">{p.name}</div>
                  <div className="text-xs font-bold text-white mt-1">{p.price_eur}€</div>
                </button>
              ))}
            </div>
            <p className="text-[9px] text-center text-[#3F3F46]">Las corcheas no caducan. Pago seguro.</p>
          </div>
        </div>
      )}

      {/* ── Publish Panel (new legal framework) ── */}
      <PublishPanel
        visible={showPublishModal}
        onClose={() => setShowPublishModal(false)}
        sessionId={sessionIdRef.current || sessionId}
        project={project}
        token={token}
        user={user}
        onPublished={() => {
          setStages(prev => ({ ...prev, published: true }));
          toast.success(lang === 'es' ? 'Tu cancion ha sido publicada.' : 'Your song has been published.');
        }}
      />
    </div>
  );
}

// ── ChatColumn (extracted for reuse in desktop + mobile) ──
function ChatColumn({ messages, sending, input, setInput, onSend, stages, lang, user, messagesEndRef, sessionId, sections, loopRegion, onExecuteAction, token, chips, genTimer, genMessage, taskPolling, compactMobile = false }) {
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const mediaRecRef = useRef(null);
  const chunksRef = useRef([]);
  const imageInputRef = useRef(null);

  const toggleVoiceRecord = async () => {
    if (recording) {
      // Stop recording
      if (mediaRecRef.current && mediaRecRef.current.state !== 'inactive') {
        mediaRecRef.current.stop();
      }
      setRecording(false);
      return;
    }
    // Start recording
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      chunksRef.current = [];
      mr.ondataavailable = e => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        if (blob.size < 500) return;
        setTranscribing(true);
        const fd = new FormData();
        fd.append('audio', blob, 'voice.webm');
        try {
          const r = await fetch(`${API}/api/producer/voice-to-text`, {
            method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
          });
          const data = await r.json();
          if (data.text) onSend(data.text);
          else toast.error(lang === 'es' ? 'No se pudo transcribir el audio. Intenta de nuevo.' : 'Could not transcribe audio. Try again.');
        } catch (e) { toast.error(lang === 'es' ? 'Error al procesar la voz' : 'Voice processing error'); }
        setTranscribing(false);
      };
      mr.start();
      mediaRecRef.current = mr;
      setRecording(true);
    } catch (e) { toast.error(lang === 'es' ? 'No se pudo acceder al microfono. Revisa los permisos.' : 'Microphone access denied. Check permissions.'); }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    const fd = new FormData();
    fd.append('image', file);
    fd.append('session_id', sessionId || '');
    fd.append('description', input || '');
    try {
      const r = await fetch(`${API}/api/producer/mood-board`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      const data = await r.json();
      if (data.ok) {
        const d = data.descriptors || {};
        const moodMsg = `[Mood Board] ${d.reference_vibe || d.description || 'Imagen analizada'}`;
        onSend(moodMsg);
        if (data.task_id) {
          toast.info(lang === 'es' ? 'PULSE esta creando musica desde tu imagen...' : 'PULSE is creating music from your image...', { duration: 8000 });
        }
      } else {
        toast.error(data.detail || 'Error analizando imagen');
      }
    } catch (e) {
      console.error('Mood board error:', e);
      toast.error('Error subiendo imagen');
    }
    setUploadingImage(false);
    if (imageInputRef.current) imageInputRef.current.value = '';
  };

  return (
    <div className="flex flex-col h-full">
      {/* Messages */}
      <div className="flex-1 overflow-y-auto space-y-2 producer-scroll pr-1" data-testid="producer-chat-messages">
        {messages.map((msg, i) => (
          <div key={i} className={`flex gap-1.5 ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            {msg.role === 'assistant' && (
              <div className="w-5 h-5 rounded-md flex-shrink-0 flex items-center justify-center text-black text-[8px] font-bold mt-0.5"
                style={{ background: 'linear-gradient(135deg, #F5C842, #FF6B00)' }}>P</div>
            )}
            <div className={`max-w-[88%] rounded-xl px-2.5 py-1.5 text-[11px] leading-relaxed ${
              msg.role === 'user' ? 'bg-[#1A1A1F] text-white rounded-tr-sm'
              : msg.isGeneration ? 'bg-[#0F0F12] border border-[#F5C84230] rounded-tl-sm'
              : msg.isPublish ? 'bg-[#0F0F12] border border-[#00D4FF30] rounded-tl-sm'
              : 'bg-[#0F0F12] border border-[#1A1A1F] rounded-tl-sm'
            }`}>
              {msg.isGeneration && (
                <div className="flex items-center gap-1 text-[#F5C842] text-[9px] font-bold mb-1">
                  <Sparkles size={8} /> GEN
                </div>
              )}
              <div dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }} className={msg.error ? 'text-red-400' : 'text-[#D4D4D8]'} />
            </div>
            {msg.role === 'user' && (
              <div className="w-5 h-5 rounded-md flex-shrink-0 bg-[#1A1A1F] flex items-center justify-center text-[8px] font-bold text-[#71717A] mt-0.5">
                {user?.name?.[0]?.toUpperCase() || 'U'}
              </div>
            )}
          </div>
        ))}
        {sending && (
          <div className="flex gap-1.5">
            <div className="w-5 h-5 rounded-md flex-shrink-0 flex items-center justify-center text-black text-[8px] font-bold"
              style={{ background: 'linear-gradient(135deg, #F5C842, #FF6B00)' }}>P</div>
            <div className="bg-[#0F0F12] border border-[#1A1A1F] rounded-xl rounded-tl-sm px-2.5 py-2 flex gap-1">
              {[0, 1, 2].map(i => (
                <div key={i} className="w-1 h-1 rounded-full bg-[#F5C842] animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
              ))}
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Generation loading overlay */}
      {taskPolling && genMessage && (
        <div data-testid="gen-loading-overlay" className="my-2 rounded-xl border border-[#F5C84230] bg-[#F5C84208] p-3 text-center">
          <div className="flex items-center justify-center gap-2 mb-1">
            <Music2 size={14} className="text-[#F5C842] animate-pulse" />
            <span className="text-[11px] font-semibold text-[#F5C842]">
              PULSE {lang === 'es' ? 'está creando' : 'is creating'}...
            </span>
            <span className="text-[11px] font-mono text-[#A1A1AA]">{genTimer}s</span>
          </div>
          <p className="text-[10px] text-[#71717A] italic">{genMessage}</p>
        </div>
      )}

      {/* Contextual chips from PULSE brain */}
      {chips && chips.length > 0 && !sending && (
        <div className={`flex gap-1 my-2 ${compactMobile ? 'overflow-x-auto whitespace-nowrap pb-1' : 'flex-wrap'}`} data-testid="pulse-chips">
          {chips.map((chip, i) => (
            <button key={i} onClick={() => onSend(chip)}
              data-testid={`chip-${i}`}
              className={`text-[9px] px-2.5 py-1.5 rounded-full border border-[#F5C84030] text-[#D4D4D8] hover:border-[#F5C84060] hover:text-[#F5C842] hover:bg-[#F5C84010] transition-all duration-200 ${compactMobile ? 'flex-shrink-0' : ''}`}>
              {chip}
            </button>
          ))}
        </div>
      )}

      {/* Quick prompts (only when session is fresh) */}
      {messages.length <= 1 && (!chips || chips.length === 0) && (
        <div className={`flex gap-1 my-2 ${compactMobile ? 'overflow-x-auto whitespace-nowrap pb-1' : 'flex-wrap'}`}>
          {QUICK_PROMPTS.map(p => (
            <button key={p} onClick={() => onSend(p)}
              className={`text-[9px] px-2 py-1 rounded-full border border-[#1A1A1F] text-[#52525B] hover:border-[#F5C84040] hover:text-[#F5C842] transition-colors ${compactMobile ? 'flex-shrink-0' : ''}`}>
              {p}
            </button>
          ))}
        </div>
      )}

      {/* Smart Buttons — contextual AI action chips */}
      {!compactMobile && (
        <div className="my-2 max-h-[180px] overflow-y-auto producer-scroll flex-shrink-0">
          <ProducerSmartButtons
            stages={stages}
            onSendMessage={onSend}
            disabled={sending}
            lang={lang}
          />
        </div>
      )}

      {/* Smart Creative Panel — REAL actions */}
      {!compactMobile && stages.base_done && (
        <div className="mt-1 mb-2 max-h-[200px] overflow-y-auto producer-scroll flex-shrink-0">
          <SmartCreativePanel
            sessionId={sessionId}
            sections={sections}
            loopRegion={loopRegion}
            onExecuteAction={onExecuteAction}
            token={token}
          />
        </div>
      )}

      {/* Input + Voice + Image */}
      <div className="bg-[#0F0F12] border border-[#1A1A1F] rounded-xl p-1.5 focus-within:border-[#F5C84030] transition-colors">
        {/* Transcribing indicator */}
        {transcribing && (
          <div className="flex items-center gap-1.5 px-2 py-1 mb-1 text-[9px] text-[#F5C842]">
            <Loader2 size={10} className="animate-spin" />
            {lang === 'es' ? 'Transcribiendo...' : 'Transcribing...'}
          </div>
        )}
        {/* Uploading image indicator */}
        {uploadingImage && (
          <div className="flex items-center gap-1.5 px-2 py-1 mb-1 text-[9px] text-[#7C3AED]">
            <Loader2 size={10} className="animate-spin" />
            {lang === 'es' ? 'Analizando imagen...' : 'Analyzing image...'}
          </div>
        )}
        <div className="flex items-end gap-1.5">
          {/* Voice toggle button */}
          <button
            data-testid="voice-input-btn"
            onClick={toggleVoiceRecord}
            disabled={transcribing}
            className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-all ${
              recording ? 'bg-red-500 animate-pulse' : transcribing ? 'bg-[#F5C84220]' : 'bg-[#1A1A1F] hover:bg-[#27272A]'
            }`}
            title={recording ? (lang === 'es' ? 'Pulsa para parar' : 'Click to stop') : (lang === 'es' ? 'Pulsa para hablar' : 'Click to speak')}
          >
            <Mic size={11} className={recording ? 'text-white' : transcribing ? 'text-[#F5C842]' : 'text-[#52525B]'} />
          </button>
          {/* Image upload for Mood Board */}
          <input ref={imageInputRef} type="file" accept="image/*" className="hidden" onChange={handleImageUpload} data-testid="mood-board-file-input" />
          <button
            data-testid="mood-board-btn"
            onClick={() => imageInputRef.current?.click()}
            disabled={uploadingImage}
            className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 bg-[#1A1A1F] hover:bg-[#27272A] transition-all"
            title={lang === 'es' ? 'Sube una imagen y PULSE creara musica desde la vibe' : 'Upload image — PULSE will create music from the vibe'}
          >
            <ImageIcon size={11} className={uploadingImage ? 'text-[#7C3AED]' : 'text-[#52525B]'} />
          </button>
          <textarea
            data-testid="producer-chat-input"
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); onSend(); } }}
            placeholder={lang === 'es' ? 'Describe tu visión...' : 'Describe your vision...'}
            rows={1}
            className="flex-1 bg-transparent text-[11px] text-white placeholder-[#3F3F46] resize-none outline-none px-1.5 py-1"
          />
          <button
            data-testid="producer-send-btn"
            onClick={() => onSend()}
            disabled={!input.trim() || sending}
            className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 transition-all"
            style={{ background: input.trim() && !sending ? 'linear-gradient(135deg, #F5C842, #FF6B00)' : '#1A1A1F' }}
          >
            <Send size={11} className={input.trim() && !sending ? 'text-black' : 'text-[#3F3F46]'} />
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Steps Row (compact horizontal) ──
function StepsRow({ stages, generating, onGenerate, wallet, onBuy, onPublish, onIterate }) {
  return (
    <div className="flex items-center gap-1.5" data-testid="creation-steps-panel">
      {STEPS.map((step, idx) => {
        const isDone = stages[step.doneKey];
        const prevDone = idx === 0 || stages[STEPS[idx - 1].doneKey];
        const isCurrent = prevDone && !isDone;
        const StepIcon = step.Icon;

        return (
          <React.Fragment key={step.key}>
            {idx > 0 && (
              <div className="w-4 h-px" style={{ background: isDone || (prevDone && isCurrent) ? '#F5C842' : '#1A1A1F' }} />
            )}
            <button
              data-testid={`step-${step.key}`}
              onClick={() => {
                if (isCurrent) {
                  onGenerate(step.key);
                }
              }}
              disabled={!isCurrent || generating !== null}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border transition-all text-[10px] font-bold ${
                isDone ? 'border-green-500/30 bg-green-500/10 text-green-400'
                : isCurrent ? 'border-[#F5C84050] bg-[#F5C84010] text-[#F5C842] hover:bg-[#F5C84020]'
                : 'border-[#1A1A1F] text-[#3F3F46] opacity-50'
              }`}
            >
              {isDone ? <CheckCircle2 size={10} />
              : isCurrent && generating === step.key ? <RefreshCw size={10} className="animate-spin" />
              : isCurrent ? <StepIcon size={10} />
              : <Lock size={9} />}
              <span>{step.name}</span>
            </button>
          </React.Fragment>
        );
      })}

      {/* Iterate — appears when base is done */}
      {stages.base_done && !stages.published && (
        <>
          <div className="w-4 h-px bg-[#1A1A1F]" />
          <button
            data-testid="iterate-btn"
            onClick={() => onIterate?.()}
            disabled={generating !== null}
            className={`flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-bold border transition-all ${
              generating === 'iterate'
                ? 'border-[#FF6B0050] bg-[#FF6B0010] text-[#FF6B00]'
                : 'border-[#FF6B0040] bg-[#FF6B0010] text-[#FF6B00] hover:bg-[#FF6B0020]'
            }`}
          >
            {generating === 'iterate' ? <RefreshCw size={10} className="animate-spin" /> : <RefreshCw size={10} />}
            Iterar
          </button>
        </>
      )}

      {/* Publish */}
      {stages.base_done && !stages.published && (
        <>
          <div className="w-4 h-px bg-[#1A1A1F]" />
          <button
            data-testid="publish-song-btn"
            onClick={() => onPublish?.()}
            className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[10px] font-bold text-white border border-[#00D4FF40] bg-[#00D4FF10] hover:bg-[#00D4FF20] transition-colors"
          >
            <Globe size={10} /> Publicar
          </button>
        </>
      )}

      {stages.published && (
        <div className="flex items-center gap-1 text-green-400 text-[10px] font-bold ml-2">
          <CheckCircle2 size={10} /> Publicada
        </div>
      )}
    </div>
  );
}

// ── WalletMini ──
function WalletMini({ wallet, adsToday, adsLeft, claimingAd, onClaimAd, onBuy }) {
  return (
    <div className="bg-[#0F0F12] rounded-xl p-3 border border-[#1A1A1F]" data-testid="corcheas-wallet-panel">
      <div className="flex items-center justify-between mb-3">
        <span className="text-[9px] text-[#3F3F46] uppercase tracking-wider font-bold">Studio</span>
        <button data-testid="buy-corcheas-btn" onClick={onBuy}
          className="text-[9px] px-2.5 py-1 rounded-md bg-[#F5C84210] text-[#F5C842] border border-[#F5C84020] hover:bg-[#F5C84220] transition-colors font-bold">
          + Recargar
        </button>
      </div>
      {/* Ads progress */}
      <div className="flex gap-0.5 mb-2">
        {[0, 1, 2, 3, 4].map(i => (
          <div key={i} className="h-0.5 flex-1 rounded-full transition-all" style={{ background: i < adsToday ? '#F5C842' : '#1A1A1F' }} />
        ))}
      </div>
      <button data-testid="claim-ad-reward-btn" onClick={onClaimAd} disabled={claimingAd || adsLeft <= 0}
        className="w-full py-1.5 rounded-lg text-[10px] font-bold transition-all flex items-center justify-center gap-1"
        style={{
          background: adsLeft > 0 && !claimingAd ? 'linear-gradient(135deg, #F5C842, #FF6B00)' : '#1A1A1F',
          color: adsLeft > 0 && !claimingAd ? '#000' : '#3F3F46',
        }}>
        {claimingAd ? <><RefreshCw size={9} className="animate-spin" />Reproduciendo...</>
        : adsLeft > 0 ? <><Zap size={9} />Ver anuncio +4 ({adsLeft} hoy)</>
        : <><Clock size={9} />Manana</>}
      </button>
    </div>
  );
}
